import { Component, ElementRef, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import {
  DwAttributeType,
  DwComponentType,
  DwControlType,
  DwFieldAggregate,
  DwFilterType,
  DwMetaDataFilterItemConfig,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwModalButtonStandardConfigs,
  DwModalConfig,
  DwModalService,
  DwModalSize,
  DwQueryFilterGroup,
  DwUiConfigRegistryService
} from '@devwareapps/devware-cap';
import { Subscription } from 'rxjs';
import { FilterType } from '../../../../shared/models/filter-type.enum';
import { ReportDashboardWidgetConfigRecord } from '../../models/report-dashboard-config-record.model';
import {
  ReportAggregateMeasure,
  ReportComputedMeasure,
  ReportDashboardDefinition,
  ReportDashboardFilterDefinition,
  ReportDrillDownDefinition,
  ReportMetricTileDefinition,
  ReportWidgetDefinition
} from '../../models/report-dashboard-definition.model';
import { ReportWidgetResult } from '../../models/report-dashboard-runtime.model';
import { ReportDashboardRepositoryService } from '../../services/report-dashboard-repository.service';
import {
  ReportDashboardFilterDesignerModalComponent,
  ReportDashboardFilterDesignerModalResult
} from '../report-dashboard-filter-designer-modal/report-dashboard-filter-designer-modal.component';

type DesignerSelectionType = 'page' | 'tile' | 'widget';
type GalleryTab = 'predefined' | 'type';

interface DashboardDesignerField {
  label: string;
  value: string;
  displayField?: string;
  numeric: boolean;
  dimensionEligible: boolean;
}

interface DashboardDesignerSelection {
  type: DesignerSelectionType;
  index?: number;
}

interface DashboardWidgetType {
  key: string;
  display: string;
  description: string;
  iconClass: string;
  chartType?: string;
  type: 'chart' | 'kpi' | 'custom';
}

@Component({
  selector: 'app-report-dashboard-design-canvas',
  templateUrl: './report-dashboard-design-canvas.component.html',
  styleUrls: ['./report-dashboard-design-canvas.component.scss']
})
export class ReportDashboardDesignCanvasComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('designSurface') designSurface: ElementRef<HTMLElement>;
  @ViewChild('quickInspector') quickInspector: ElementRef<HTMLElement>;
  @Input() definition: ReportDashboardDefinition;
  @Input() reusableWidgetRecords: ReportDashboardWidgetConfigRecord[] = [];
  @Input() previewFilters = new DwQueryFilterGroup();
  @Input() expressionContext: any;
  @Input() contextNames: string[] = [];
  @Output() definitionChange = new EventEmitter<ReportDashboardDefinition>();
  @Output() jsonRequested = new EventEmitter<void>();

  selection: DashboardDesignerSelection = { type: 'page' };
  inspectorTab = 'data';
  galleryTab: GalleryTab = 'predefined';
  galleryVisible = false;
  selectedReusableWidget: ReportDashboardWidgetConfigRecord;
  selectedWidgetType: DashboardWidgetType;
  fieldOptions: DashboardDesignerField[] = [];
  numericFieldOptions: DashboardDesignerField[] = [];
  customComponentOptions: Array<{ label: string; value: string }> = [];
  metadataLoading = false;
  metadataError: string;
  draggedSelection: DashboardDesignerSelection;
  inspectorVisible = false;
  inspectorTop = 12;
  inspectorLeft = 12;
  inspectorSide: 'left' | 'right' = 'right';
  livePreview = true;
  previewLoading = false;
  previewError: string;
  previewResults: { [key: string]: ReportWidgetResult } = {};
  showCategoricalDimensionFields = false;
  categoryOrderText = '';

  private dragPreviewElement: HTMLElement;
  private dragOrderChanged = false;
  private previewTimer: any;
  private previewSubscription: Subscription;
  private previewGeneration = 0;

  readonly chartTypes = [
    { value: 'bar', display: 'Column', icon: 'fas fa-chart-bar' },
    { value: 'column', display: 'Bar', icon: 'fas fa-align-left' },
    { value: 'line', display: 'Line', icon: 'fas fa-chart-line' },
    { value: 'pie', display: 'Pie', icon: 'fas fa-chart-pie' },
    { value: 'doughnut', display: 'Donut', icon: 'fas fa-circle-notch' }
  ];

  readonly aggregateTypes = [
    { label: 'Count rows', value: DwFieldAggregate.CountAll },
    { label: 'Count distinct rows', value: DwFieldAggregate.CountAllDistinct },
    { label: 'Count field', value: DwFieldAggregate.Count },
    { label: 'Count distinct field', value: DwFieldAggregate.CountDistinct },
    { label: 'Sum', value: DwFieldAggregate.Sum },
    { label: 'Average', value: DwFieldAggregate.Avg },
    { label: 'Minimum', value: DwFieldAggregate.Min },
    { label: 'Maximum', value: DwFieldAggregate.Max }
  ];

  readonly spanOptions = [3, 4, 6, 8, 12];

  readonly widgetTypes: DashboardWidgetType[] = [
    {
      key: 'kpi',
      display: 'KPI stat',
      description: 'A headline value with supporting text',
      iconClass: 'fa fa-hashtag',
      type: 'kpi'
    },
    {
      key: 'column',
      display: 'Column chart',
      description: 'Vertical columns for category comparisons',
      iconClass: 'fas fa-chart-bar',
      chartType: 'bar',
      type: 'chart'
    },
    {
      key: 'bar',
      display: 'Bar chart',
      description: 'Horizontal bars for longer category labels',
      iconClass: 'fas fa-align-left',
      chartType: 'column',
      type: 'chart'
    },
    {
      key: 'line',
      display: 'Line chart',
      description: 'A trend across an ordered dimension',
      iconClass: 'fas fa-chart-line',
      chartType: 'line',
      type: 'chart'
    },
    {
      key: 'doughnut',
      display: 'Donut chart',
      description: 'Part-to-whole category composition',
      iconClass: 'fas fa-circle-notch',
      chartType: 'doughnut',
      type: 'chart'
    },
    {
      key: 'custom',
      display: 'Custom component',
      description: 'A component registered through UI Config',
      iconClass: 'fa fa-puzzle-piece',
      type: 'custom'
    }
  ];

  constructor(
    @Inject(DwMetaDataServiceToken) private metaDataService: DwMetaDataService,
    private uiConfigRegistry: DwUiConfigRegistryService,
    private modalService: DwModalService,
    private repository: ReportDashboardRepositoryService
  ) { }

  ngOnInit(): void {
    this.loadCustomComponents();
    this.ensureDefinitionCollections();
    this.loadFieldsForSelection();
    this.schedulePreviewReload(0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.definition) {
      this.ensureDefinitionCollections();
      this.ensureSelectionExists();
      this.syncCategoryOrderText();
      this.loadFieldsForSelection();
      this.schedulePreviewReload();
    }
    if (changes.previewFilters && !changes.previewFilters.firstChange) {
      this.schedulePreviewReload(0);
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.previewTimer);
    this.previewSubscription?.unsubscribe();
  }

  get selectedWidget(): ReportWidgetDefinition {
    return this.selection.type === 'widget'
      ? this.definition?.widgets?.[this.selection.index]
      : null;
  }

  get layoutColumns(): number[] {
    const count = this.definition?.layoutColumnCount || 12;
    return Array.from({ length: count }, (_value, index) => index + 1);
  }

  get selectedReusableReference(): any {
    const widget: any = this.selectedWidget;
    return widget?.widgetKey ? widget : null;
  }

  get selectedTile(): ReportMetricTileDefinition {
    return this.selection.type === 'tile'
      ? this.definition?.tiles?.[this.selection.index]
      : null;
  }

  get defaultDrillDownDefinition(): ReportDrillDownDefinition {
    const key = this.definition?.defaultDrillDown?.drillDownKey || this.definition?.defaultDrillDownKey;
    return (this.definition?.drillDowns || []).find(drillDown => drillDown.key === key)
      || this.definition?.drillDowns?.[0];
  }

  get selectedTitle(): string {
    if (this.selectedWidget) {
      return this.widgetTitle(this.selectedWidget);
    }
    if (this.selectedTile) {
      return this.selectedTile.tileDisplay;
    }
    return this.definition?.title || 'Dashboard';
  }

  get selectedWidgetItem(): string {
    const resolvedWidget: any = this.resolveWidget(this.selectedWidget as any);
    return resolvedWidget?.request?.queryRequest?.item
      || this.definition?.dataSourceItemName
      || 'roCheckrideFactBase';
  }

  get dimensionFieldOptions(): DashboardDesignerField[] {
    const currentValue = this.selectedWidget?.request?.categoryField;
    const availableFields = this.showCategoricalDimensionFields
      ? this.fieldOptions.filter(field => field.dimensionEligible)
      : this.numericFieldOptions;
    if (!currentValue || availableFields.some(field => field.value === currentValue)) {
      return availableFields;
    }
    const currentField = this.fieldOptions.find(field => field.value === currentValue);
    return currentField
      ? [{ ...currentField, label: `${currentField.label} (current advanced field)` }, ...availableFields]
      : [{
        label: `${currentValue} (current advanced field)`,
        value: currentValue,
        numeric: false,
        dimensionEligible: true
      }, ...availableFields];
  }

  get aggregateMeasureAliasOptions(): Array<{ label: string; value: string }> {
    return (this.selectedWidget?.measures || [])
      .filter(measure => !!measure.alias)
      .map(measure => ({ label: measure.legendDisplay || measure.alias, value: measure.alias }));
  }

  widgetTitle(widget: any): string {
    const resolved = this.resolveWidget(widget);
    return resolved?.title || widget?.widgetKey || 'Untitled widget';
  }

  widgetDescription(widget: any): string {
    return this.resolveWidget(widget)?.description;
  }

  widgetType(widget: any): string {
    return this.resolveWidget(widget)?.type;
  }

  widgetRequest(widget: any): any {
    return this.resolveWidget(widget)?.request;
  }

  widgetColumnSpan(widget: any): number {
    const resolved = this.resolveWidget(widget);
    const defaultSpan = Math.max(1, Math.floor((this.definition?.layoutColumnCount || 12) / (this.definition?.columnCount || 2)));
    return widget?.configOverrides?.columnSpan || resolved?.columnSpan || defaultSpan;
  }

  setReusableWidgetSpan(span: number): void {
    if (!this.selectedReusableReference) {
      return;
    }
    this.selectedReusableReference.configOverrides = this.selectedReusableReference.configOverrides || {};
    this.selectedReusableReference.configOverrides.columnSpan = span;
    this.notifyChanged();
  }

  selectPage(): void {
    this.selection = { type: 'page' };
    this.inspectorTab = 'display';
    this.inspectorVisible = false;
  }

  selectTile(index: number, event?: Event): void {
    event?.stopPropagation();
    this.selection = { type: 'tile', index };
    this.inspectorTab = 'display';
  }

  selectWidget(index: number, event?: Event): void {
    event?.stopPropagation();
    this.selection = { type: 'widget', index };
    this.inspectorTab = 'data';
    this.showCategoricalDimensionFields = false;
    this.syncCategoryOrderText();
    this.loadFieldsForSelection();
  }

  configurePage(event: Event): void {
    event.stopPropagation();
    this.selection = { type: 'page' };
    this.inspectorTab = 'display';
    this.openInspector(this.findInspectorAnchor(event.currentTarget as HTMLElement));
  }

  configureTile(index: number, event: Event): void {
    this.selectTile(index, event);
    this.openInspector(this.findInspectorAnchor(event.currentTarget as HTMLElement));
  }

  configureWidget(index: number, event: Event): void {
    this.selectWidget(index, event);
    this.openInspector(this.findInspectorAnchor(event.currentTarget as HTMLElement));
  }

  closeInspector(event?: Event): void {
    event?.stopPropagation();
    this.inspectorVisible = false;
  }

  selectInspectorTab(tab: string): void {
    this.inspectorTab = tab;
  }

  notifyChanged(): void {
    this.definitionChange.emit(this.definition);
    this.schedulePreviewReload();
  }

  toggleLivePreview(): void {
    this.livePreview = !this.livePreview;
    this.previewError = null;
    if (this.livePreview) {
      this.schedulePreviewReload(0);
    } else {
      clearTimeout(this.previewTimer);
      this.previewGeneration++;
      this.previewLoading = false;
      this.previewSubscription?.unsubscribe();
    }
  }

  previewResult(widget: any): ReportWidgetResult {
    return this.previewResults[this.resolveWidget(widget)?.key];
  }

  canPreviewWidget(widget: any): boolean {
    const resolved = this.resolveWidget(widget);
    if (!resolved || resolved.type !== 'chart' || !resolved.request?.categoryField || !resolved.request?.queryRequest?.item) {
      return false;
    }
    return !!resolved.measures?.length && resolved.measures.every(measure =>
      !this.aggregateRequiresField(measure.aggregateFunction) || !!measure.field
    );
  }

  enableDefaultDrillDown(): void {
    const key = 'details';
    this.definition.drillDowns = this.definition.drillDowns || [];
    if (!this.definition.drillDowns.length) {
      this.definition.drillDowns.push({
        key,
        title: 'Details',
        item: '',
        targetKeyField: '',
        gridConfigKey: ''
      });
    }

    const drillDown = this.definition.drillDowns[0];
    this.definition.defaultDrillDownKey = drillDown.key;
    this.definition.defaultDrillDown = {
      drillDownKey: drillDown.key,
      sourceKeyField: ''
    };
    this.notifyChanged();
  }

  onDefaultDrillDownKeyChanged(key: string): void {
    this.definition.defaultDrillDownKey = key;
    this.definition.defaultDrillDown = this.definition.defaultDrillDown || {
      drillDownKey: key,
      sourceKeyField: ''
    };
    this.definition.defaultDrillDown.drillDownKey = key;
    this.notifyChanged();
  }

  setTileDrillDownKey(key: string): void {
    if (!key) {
      delete this.selectedTile.additionalConfig.drillDown;
      this.notifyChanged();
      return;
    }
    this.selectedTile.additionalConfig.drillDown = {
      drillDownKey: key,
      sourceKeyField: this.selectedTile.additionalConfig.drillDown?.sourceKeyField
        || this.definition.defaultDrillDown?.sourceKeyField
        || ''
    };
    this.notifyChanged();
  }

  setWidgetDrillDownKey(key: string): void {
    if (!key) {
      delete this.selectedWidget.drillDown;
      this.notifyChanged();
      return;
    }
    this.selectedWidget.drillDown = {
      drillDownKey: key,
      sourceKeyField: this.selectedWidget.drillDown?.sourceKeyField
        || this.definition.defaultDrillDown?.sourceKeyField
        || ''
    };
    this.notifyChanged();
  }

  onWidgetItemChanged(): void {
    if (!this.selectedWidget.request.queryRequest) {
      this.selectedWidget.request.queryRequest = {} as any;
    }
    this.loadFieldsForSelection();
    this.notifyChanged();
  }

  setChartType(chartType: string): void {
    if (!this.selectedWidget?.request) {
      return;
    }
    this.selectedWidget.request.chartType = chartType as any;
    this.notifyChanged();
  }

  onDimensionChanged(fieldValue: string): void {
    if (!this.selectedWidget?.request) {
      return;
    }
    this.selectedWidget.request.categoryField = fieldValue;
    const field = this.fieldOptions.find(option => option.value === fieldValue);
    this.selectedWidget.request.categoryDisplayField = field?.displayField;
    this.notifyChanged();
  }

  setCategoryOrder(value: string): void {
    const categories = (value || '').split(',').map(item => item.trim()).filter(item => !!item);
    if (categories.length) {
      this.selectedWidget.categoryOrder = categories;
    } else {
      delete this.selectedWidget.categoryOrder;
    }
    this.categoryOrderText = categories.join(', ');
    this.notifyChanged();
  }

  onMeasureAggregateChanged(measure: ReportAggregateMeasure, aggregate: DwFieldAggregate): void {
    measure.aggregateFunction = aggregate;
    if (!this.aggregateRequiresField(aggregate)) {
      measure.field = undefined;
    }
    this.notifyChanged();
  }

  aggregateRequiresField(aggregate: DwFieldAggregate): boolean {
    return aggregate !== DwFieldAggregate.CountAll && aggregate !== DwFieldAggregate.CountAllDistinct;
  }

  addMeasure(): void {
    if (!this.selectedWidget) {
      return;
    }
    const measureNumber = (this.selectedWidget.measures?.length || 0) + 1;
    this.selectedWidget.measures = this.selectedWidget.measures || [];
    this.selectedWidget.measures.push({
      alias: `Measure${measureNumber}`,
      aggregateFunction: DwFieldAggregate.Count,
      field: '',
      legendDisplay: `Measure ${measureNumber}`,
      showInChart: true
    });
    this.notifyChanged();
  }

  removeMeasure(index: number): void {
    this.selectedWidget?.measures?.splice(index, 1);
    this.notifyChanged();
  }

  addComputedMeasure(): void {
    if (!this.selectedWidget) {
      return;
    }
    const aliases = (this.selectedWidget.measures || []).map(measure => measure.alias).filter(alias => !!alias);
    const computedNumber = (this.selectedWidget.computedMeasures?.length || 0) + 1;
    const computed: ReportComputedMeasure = {
      alias: `Computed${computedNumber}`,
      numeratorField: aliases[0] || '',
      denominatorField: aliases[1] || aliases[0] || '',
      multiplier: 100,
      decimals: 0,
      legendDisplay: `Computed ${computedNumber}`,
      valueSuffix: '%',
      showInChart: true
    };
    this.selectedWidget.computedMeasures = this.selectedWidget.computedMeasures || [];
    this.selectedWidget.computedMeasures.push(computed);
    this.notifyChanged();
  }

  removeComputedMeasure(index: number): void {
    this.selectedWidget?.computedMeasures?.splice(index, 1);
    this.notifyChanged();
  }

  duplicateSelected(): void {
    if (this.selectedWidget) {
      const copy = this.clone(this.selectedWidget);
      copy.key = this.uniqueKey(`${copy.key}-copy`, this.definition.widgets.map(widget => widget.key));
      copy.title = `${copy.title} Copy`;
      this.definition.widgets.splice(this.selection.index + 1, 0, copy);
      this.selection = { type: 'widget', index: this.selection.index + 1 };
    } else if (this.selectedTile) {
      const copy = this.clone(this.selectedTile);
      copy.tileKey = this.uniqueKey(`${copy.tileKey}-copy`, this.definition.tiles.map(tile => tile.tileKey));
      copy.tileDisplay = `${copy.tileDisplay} Copy`;
      this.definition.tiles.splice(this.selection.index + 1, 0, copy);
      this.selection = { type: 'tile', index: this.selection.index + 1 };
    }
    this.notifyChanged();
  }

  deleteSelected(): void {
    if (this.selectedWidget) {
      this.definition.widgets.splice(this.selection.index, 1);
    } else if (this.selectedTile) {
      this.definition.tiles.splice(this.selection.index, 1);
    }
    this.selection = { type: 'page' };
    this.inspectorVisible = false;
    this.notifyChanged();
  }

  beginDrag(type: 'tile' | 'widget', index: number, event: DragEvent): void {
    this.draggedSelection = { type, index };
    this.dragOrderChanged = false;
    this.inspectorVisible = false;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', `${type}:${index}`);
      const dragHandle = event.currentTarget as HTMLElement;
      const source = dragHandle.closest('[data-designer-type]') as HTMLElement || dragHandle;
      const bounds = source.getBoundingClientRect();
      this.removeDragPreview();
      this.dragPreviewElement = source.cloneNode(true) as HTMLElement;
      this.dragPreviewElement.classList.add('dashboard-drag-preview');
      this.dragPreviewElement.style.width = `${bounds.width}px`;
      this.dragPreviewElement.style.height = `${bounds.height}px`;
      document.body.appendChild(this.dragPreviewElement);
      event.dataTransfer.setDragImage(
        this.dragPreviewElement,
        Math.min(Math.max(event.clientX - bounds.left, 12), bounds.width - 12),
        Math.min(Math.max(event.clientY - bounds.top, 12), bounds.height - 12)
      );
    }
  }

  endDrag(): void {
    const orderChanged = this.dragOrderChanged;
    this.draggedSelection = null;
    this.dragOrderChanged = false;
    this.removeDragPreview();
    if (orderChanged) {
      this.schedulePreviewReload();
    }
  }

  allowDrop(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  }

  dropOn(type: 'tile' | 'widget', index: number, event: DragEvent): void {
    event.preventDefault();
    if (!this.draggedSelection || this.draggedSelection.type !== type) {
      return;
    }
    this.previewDragPosition(type, index, event);
    this.endDrag();
  }

  previewDragPosition(type: 'tile' | 'widget', index: number, event: DragEvent): void {
    event.preventDefault();
    if (!this.draggedSelection || this.draggedSelection.type !== type
      || this.draggedSelection.index === index) {
      return;
    }
    const sourceIndex = this.draggedSelection.index;
    const collection: any[] = type === 'widget' ? this.definition.widgets : this.definition.tiles;
    const moved = collection.splice(sourceIndex, 1)[0];
    collection.splice(index, 0, moved);
    this.selection = { type, index };
    this.draggedSelection = { type, index };
    this.dragOrderChanged = true;
    // Mark the dashboard dirty immediately, but defer live chart re-querying
    // until the drag gesture ends.
    this.definitionChange.emit(this.definition);
  }

  openGallery(tab: GalleryTab = 'predefined'): void {
    this.galleryTab = tab;
    this.galleryVisible = true;
    this.selectedReusableWidget = null;
    this.selectedWidgetType = null;
  }

  closeGallery(): void {
    this.galleryVisible = false;
  }

  selectReusableWidget(record: ReportDashboardWidgetConfigRecord): void {
    this.selectedReusableWidget = record;
    this.selectedWidgetType = null;
  }

  selectWidgetType(type: DashboardWidgetType): void {
    this.selectedWidgetType = type;
    this.selectedReusableWidget = null;
  }

  addGallerySelection(): void {
    if (this.galleryTab === 'predefined' && this.selectedReusableWidget) {
      this.addReusableWidget(this.selectedReusableWidget);
    } else if (this.galleryTab === 'type' && this.selectedWidgetType) {
      this.addWidgetFromType(this.selectedWidgetType);
    } else {
      return;
    }
    this.galleryVisible = false;
    this.notifyChanged();
    setTimeout(() => this.openInspectorForSelection());
  }

  reusableWidgetTitle(record: ReportDashboardWidgetConfigRecord): string {
    return this.parseReusableWidget(record)?.title || record.WidgetDisplay;
  }

  reusableWidgetDescription(record: ReportDashboardWidgetConfigRecord): string {
    return this.parseReusableWidget(record)?.description || 'Reusable dashboard widget';
  }

  reusableWidgetIcon(record: ReportDashboardWidgetConfigRecord): string {
    const definition = this.parseReusableWidget(record);
    return definition?.type === 'custom' ? 'fas fa-puzzle-piece' : 'fas fa-chart-bar';
  }

  selectedReusablePreview(): any {
    return this.parseReusableWidget(this.selectedReusableWidget);
  }

  chartTypeIcon(chartType: any): string {
    return this.chartTypes.find(type => type.value === chartType)?.icon || 'fas fa-chart-bar';
  }

  requestJsonEditor(): void {
    this.jsonRequested.emit();
  }

  openFilterBuilder(): void {
    if (!this.definition?.dataSourceItemName) {
      this.metadataError = 'Set the dashboard default data source before configuring filters.';
      this.selectPage();
      return;
    }

    const modalConfig = new DwModalConfig();
    modalConfig.title = 'Design Dashboard Filters';
    modalConfig.component = ReportDashboardFilterDesignerModalComponent;
    modalConfig.buttons = DwModalButtonStandardConfigs.instance.SaveAndCancel;
    modalConfig.modalSize = DwModalSize.custom;
    modalConfig.customModalWidth = '1500px';
    modalConfig.customModalHeight = '850px';
    modalConfig.allowFullScreen = true;
    modalConfig.cloneData = true;
    modalConfig.data = {
      itemName: this.definition.dataSourceItemName,
      contextNames: this.contextNames || [],
      filters: (this.definition.filters || []).map(filter => filter.config),
      dataSourceItemNames: this.getEffectiveDataSourceItemNames(),
      globalFilters: this.definition.globalFilters || []
    };

    this.modalService.showModal(modalConfig).subscribe(result => {
      if (result.cancelled) {
        return;
      }
      const modalResult = result.data as ReportDashboardFilterDesignerModalResult;
      this.definition.globalFilters = modalResult?.globalFilters || [];
      this.notifyChanged();
      this.applyFilterBuilderResult(modalResult?.filters || []);
    });
  }

  private getEffectiveDataSourceItemNames(): string[] {
    const itemNames = [
      this.definition?.dataSourceItemName,
      ...(this.definition?.summaries || []).map(summary =>
        summary.dataSourceItemName || this.definition?.dataSourceItemName
      ),
      ...(this.definition?.widgets || []).map(widget => {
        const resolved = this.resolveWidget(widget as any);
        return resolved?.request?.queryRequest?.item || this.definition?.dataSourceItemName;
      }),
      ...(this.definition?.globalFilters || []).map(filter => filter.dataSourceItemName)
    ];

    return itemNames.filter((itemName, index, source) => !!itemName
      && source.findIndex(item => item?.toLowerCase() === itemName.toLowerCase()) === index);
  }

  private addReusableWidget(record: ReportDashboardWidgetConfigRecord): void {
    const reusableDefinition = this.parseReusableWidget(record);
    const baseKey = reusableDefinition?.key || record.WidgetKey;
    const key = this.uniqueKey(baseKey, this.definition.widgets.map(widget => widget.key));
    (this.definition.widgets as any[]).push({
      key,
      widgetKey: record.WidgetKey,
      configOverrides: {
        columnSpan: reusableDefinition?.columnSpan || 6
      }
    });
    this.selection = { type: 'widget', index: this.definition.widgets.length - 1 };
    this.syncCategoryOrderText();
  }

  private addWidgetFromType(type: DashboardWidgetType): void {
    if (type.type === 'kpi') {
      this.addKpi();
      return;
    }

    const item = this.definition.dataSourceItemName || 'roCheckrideFactBase';
    const key = this.uniqueKey(`new-${type.key}`, this.definition.widgets.map(widget => widget.key));
    const widget: ReportWidgetDefinition = type.type === 'custom'
      ? {
        key,
        title: 'New Custom Widget',
        type: 'custom',
        componentName: this.customComponentOptions[0]?.value,
        componentConfig: {},
        columnSpan: 6,
        minimumHeightPixels: 280
      }
      : {
        key,
        title: `New ${type.display}`,
        description: type.description,
        type: 'chart',
        request: {
          chartType: type.chartType as any,
          title: '',
          categoryField: '',
          hideLegend: false,
          queryRequest: { item } as any
        },
        measures: [{
          alias: 'Total',
          aggregateFunction: DwFieldAggregate.Count,
          field: '',
          legendDisplay: 'Total',
          showInChart: true
        }],
        computedMeasures: [],
        maxItems: 15,
        gridMaxItems: 100,
        showGridTotals: true,
        sortField: 'Total',
        sortDescending: true,
        showValues: true,
        columnSpan: 6,
        minimumHeightPixels: 320
      };

    this.definition.widgets.push(widget);
    this.selection = { type: 'widget', index: this.definition.widgets.length - 1 };
    this.inspectorTab = 'data';
    this.syncCategoryOrderText();
    this.loadFieldsForSelection();
  }

  private addKpi(): void {
    this.definition.summaries = this.definition.summaries || [];
    this.definition.tiles = this.definition.tiles || [];
    const summaryKey = this.uniqueKey('new-kpi-summary', this.definition.summaries.map(summary => summary.key));
    const tileKey = this.uniqueKey('new-kpi', this.definition.tiles.map(tile => tile.tileKey));
    this.definition.summaries.push({
      key: summaryKey,
      dataSourceItemName: this.definition.dataSourceItemName || 'roCheckrideFactBase',
      measures: [{
        alias: 'Value',
        aggregateFunction: DwFieldAggregate.Count,
        field: ''
      }]
    });
    this.definition.tiles.push({
      tileKey,
      tileDisplay: 'New KPI',
      showFilter: true,
      additionalConfig: {
        summaryKey,
        value: { field: 'Value' },
        format: 'number'
      }
    } as any);
    this.selection = { type: 'tile', index: this.definition.tiles.length - 1 };
    this.inspectorTab = 'display';
  }

  private openInspectorForSelection(): void {
    if (this.selection.type === 'page') {
      return;
    }
    const selector = `[data-designer-type="${this.selection.type}"][data-designer-index="${this.selection.index}"]`;
    const anchor = this.designSurface?.nativeElement.querySelector(selector) as HTMLElement;
    if (!anchor) {
      return;
    }
    this.syncCategoryOrderText();
    anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
    this.openInspector(anchor);
  }

  private openInspector(anchor: HTMLElement): void {
    if (!anchor || !this.designSurface?.nativeElement) {
      return;
    }
    this.inspectorVisible = true;
    setTimeout(() => this.positionInspector(anchor));
  }

  private positionInspector(anchor: HTMLElement): void {
    const surface = this.designSurface?.nativeElement;
    if (!surface || !anchor) {
      return;
    }

    const surfaceBounds = surface.getBoundingClientRect();
    const anchorBounds = anchor.getBoundingClientRect();
    const inspectorWidth = this.quickInspector?.nativeElement?.offsetWidth || 368;
    const gutter = 12;
    const rightPosition = anchorBounds.right - surfaceBounds.left + gutter;
    const leftPosition = anchorBounds.left - surfaceBounds.left - inspectorWidth - gutter;
    const hasRoomOnRight = rightPosition + inspectorWidth <= surfaceBounds.width - gutter;
    const hasRoomOnLeft = leftPosition >= gutter;

    if (hasRoomOnRight || !hasRoomOnLeft) {
      this.inspectorSide = 'right';
      this.inspectorLeft = Math.max(gutter, Math.min(rightPosition, surfaceBounds.width - inspectorWidth - gutter));
    } else {
      this.inspectorSide = 'left';
      this.inspectorLeft = leftPosition;
    }
    this.inspectorTop = Math.max(gutter, anchorBounds.top - surfaceBounds.top);
  }

  private findInspectorAnchor(element: HTMLElement): HTMLElement {
    const surface = this.designSurface?.nativeElement;
    let current = element;
    while (current && current !== surface) {
      if (current.classList.contains('canvas-page-header')
        || current.classList.contains('design-kpi-card')
        || current.classList.contains('design-widget-card')) {
        return current;
      }
      current = current.parentElement;
    }
    return element;
  }

  private removeDragPreview(): void {
    if (this.dragPreviewElement?.parentElement) {
      this.dragPreviewElement.parentElement.removeChild(this.dragPreviewElement);
    }
    this.dragPreviewElement = null;
  }

  private loadFieldsForSelection(): void {
    const widget = this.selectedWidget;
    if (!widget || widget.type !== 'chart') {
      this.fieldOptions = [];
      this.numericFieldOptions = [];
      return;
    }

    const itemName = this.selectedWidgetItem;
    if (!itemName) {
      this.fieldOptions = [];
      this.numericFieldOptions = [];
      return;
    }

    this.metadataLoading = true;
    this.metadataError = null;
    this.metaDataService.getSavedQueryMetaData(itemName, null, null, true).subscribe(queryMetaData => {
      this.fieldOptions = (queryMetaData.Attributes || []).map(attribute => {
        const attributeType = attribute.AttributeType || attribute.Attribute?.AttributeType;
        return {
          label: attribute.AttributeDisplay || attribute.Attribute?.Display || attribute.DisplayAttributeName || attribute.AttributeName,
          value: `${attribute.ItemName || itemName}.${attribute.AttributeName}`,
          displayField: attribute.HasDisplayField && attribute.DisplayAttributeName !== attribute.AttributeName
            ? attribute.DisplayAttributeName?.split('.').pop()
            : undefined,
          numeric: this.isNumericAttribute(attributeType),
          dimensionEligible: this.isDimensionAttribute(attributeType)
        };
      }).filter((field, index, fields) => fields.findIndex(item => item.value === field.value) === index)
        .sort((a, b) => a.label.localeCompare(b.label));
      this.numericFieldOptions = this.fieldOptions.filter(field => field.numeric);
      const currentDimension = this.selectedWidget?.request?.categoryField;
      if (currentDimension && this.fieldOptions.some(field => field.value === currentDimension && !field.numeric)) {
        this.showCategoricalDimensionFields = true;
      }
      this.metadataLoading = false;
    }, error => {
      this.fieldOptions = [];
      this.numericFieldOptions = [];
      this.metadataLoading = false;
      this.metadataError = error?.message || 'Fields could not be loaded for this data source.';
    });
  }

  private isNumericAttribute(attributeType: DwAttributeType): boolean {
    return [
      DwAttributeType.Integer,
      DwAttributeType.LargeInteger,
      DwAttributeType.Decimal,
      DwAttributeType.Money,
      DwAttributeType.Percentage
    ].indexOf(attributeType) >= 0;
  }

  private syncCategoryOrderText(): void {
    this.categoryOrderText = (this.selectedWidget?.categoryOrder || []).join(', ');
  }

  private isDimensionAttribute(attributeType: DwAttributeType): boolean {
    return this.isNumericAttribute(attributeType) || [
      DwAttributeType.Text,
      DwAttributeType.YesNo,
      DwAttributeType.Date,
      DwAttributeType.DateTime
    ].indexOf(attributeType) >= 0;
  }

  private schedulePreviewReload(delay = 450): void {
    clearTimeout(this.previewTimer);
    if (!this.livePreview || !this.definition) {
      return;
    }
    this.previewLoading = true;
    const generation = ++this.previewGeneration;
    this.previewTimer = setTimeout(() => this.loadPreviews(generation), delay);
  }

  private loadPreviews(generation: number): void {
    const definitions = (this.definition.widgets || [])
      .map(widget => this.clone(this.resolveWidget(widget)))
      .filter(widget => this.canPreviewWidget(widget));

    this.previewSubscription?.unsubscribe();
    if (!definitions.length) {
      this.previewResults = {};
      this.previewLoading = false;
      return;
    }

    this.previewSubscription = this.repository.loadWidgets(
      definitions,
      this.previewFilters || new DwQueryFilterGroup(),
      this.definition.globalFilters,
      this.expressionContext
    )
      .subscribe(results => {
        if (generation !== this.previewGeneration) {
          return;
        }
        this.previewResults = (results || []).reduce((lookup, result) => {
          result.chartPreferences = {
            ...result.chartPreferences,
            showContainerTitle: false
          };
          lookup[result.definition.key] = result;
          return lookup;
        }, {} as { [key: string]: ReportWidgetResult });
        this.previewError = null;
        this.previewLoading = false;
      }, error => {
        if (generation !== this.previewGeneration) {
          return;
        }
        this.previewResults = {};
        this.previewError = error?.message || 'Live chart data could not be loaded.';
        this.previewLoading = false;
      });
  }

  private loadCustomComponents(): void {
    this.customComponentOptions = this.uiConfigRegistry
      .getRegisteredComponents([DwComponentType.card, DwComponentType.pageDesignSection])
      .map(component => ({ label: component.name, value: component.key || component.name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  private applyFilterBuilderResult(filters: DwMetaDataFilterItemConfig[]): void {
    const itemName = this.definition.dataSourceItemName;
    this.metaDataService.getSavedQueryMetaData(itemName, null, null, true).subscribe(queryMetaData => {
      this.setDashboardFilters(filters, queryMetaData?.Attributes || []);
    }, () => {
      this.setDashboardFilters(filters, []);
    });
  }

  private setDashboardFilters(filters: DwMetaDataFilterItemConfig[], attributes: any[]): void {
    this.definition.filters = (filters || []).map(config => {
      return {
        config: this.normalizeFilterConfig(config, attributes)
      } as ReportDashboardFilterDefinition;
    });
    this.notifyChanged();
  }

  private normalizeFilterConfig(config: DwMetaDataFilterItemConfig, attributes: any[]): any {
    const attribute = (attributes || []).find(item => item.AttributeName === config.attr
      || `${item.RelationPrefix}.${item.AttributeName}` === config.attr);
    const metadata = attribute?.Attribute || {};
    const additionalConfig: any = { ...(config.additionalConfig || {}) };
    const controlType = config.controlTypeOverride || metadata.MetaDataControlTypeId;
    const lookupOverride = config.lookupConfig?.lookupIdOverride;
    const lookupId = additionalConfig.lookupId
      || (typeof lookupOverride === 'number' ? lookupOverride : null)
      || (typeof metadata.MetaDataLookupId === 'number' ? metadata.MetaDataLookupId : null);
    const presentationType = additionalConfig.presentationType
      || this.inferPresentationType(config, controlType, !!lookupId || config.showAsLookup === true);

    return {
      ...config,
      showFilter: config.showFilter !== false,
      additionalConfig: {
        ...additionalConfig,
        presentationType,
        lookupId: lookupId || undefined,
        allowMultiple: additionalConfig.allowMultiple || presentationType === FilterType.MultiSelect
      }
    };
  }

  private inferPresentationType(config: DwMetaDataFilterItemConfig, controlType: DwControlType, isLookup: boolean): FilterType {
    if (config.componentName === 'dwMultiSelectLookupFilter'
      || controlType === DwControlType.MultiSelect
      || controlType === DwControlType.MultiSelectAdhoc
      || (isLookup && config.filterType === DwFilterType.In)) {
      return FilterType.MultiSelect;
    }
    if (controlType === DwControlType.DatePicker || controlType === DwControlType.DateTimePicker) {
      return FilterType.DatePicker;
    }
    if (controlType === DwControlType.Checkbox || controlType === DwControlType.YesNoDropDown
      || config.filterType === DwFilterType.IsTrue || config.filterType === DwFilterType.IsFalse) {
      return FilterType.Toggle;
    }
    if (isLookup) {
      return FilterType.Dropdown;
    }
    return FilterType.TextInput;
  }

  private ensureDefinitionCollections(): void {
    if (!this.definition) {
      return;
    }
    this.definition.filters = this.definition.filters || [];
    this.definition.summaries = this.definition.summaries || [];
    this.definition.tiles = this.definition.tiles || [];
    this.definition.widgets = this.definition.widgets || [];
    this.definition.drillDowns = this.definition.drillDowns || [];
    if (this.definition.drillDowns.length && !this.definition.defaultDrillDown) {
      const defaultKey = this.definition.defaultDrillDownKey || this.definition.drillDowns[0].key;
      this.definition.defaultDrillDownKey = defaultKey;
      this.definition.defaultDrillDown = {
        drillDownKey: defaultKey,
        sourceKeyField: ''
      };
    }
    if (!this.definition.layoutColumnCount) {
      this.definition.layoutColumnCount = 12;
      const defaultSpan = Math.max(1, Math.floor(12 / (this.definition.columnCount || 2)));
      this.definition.widgets.forEach((widget: any) => {
        if (!widget.columnSpan && !widget.configOverrides?.columnSpan) {
          if (widget.widgetKey) {
            widget.configOverrides = widget.configOverrides || {};
            widget.configOverrides.columnSpan = defaultSpan;
          } else {
            widget.columnSpan = defaultSpan;
          }
        }
      });
    }
  }

  private ensureSelectionExists(): void {
    if (!this.definition) {
      this.selection = { type: 'page' };
      return;
    }
    if (this.selection.type === 'widget' && !this.definition.widgets?.[this.selection.index]) {
      this.selection = { type: 'page' };
    }
    if (this.selection.type === 'tile' && !this.definition.tiles?.[this.selection.index]) {
      this.selection = { type: 'page' };
    }
  }

  private parseReusableWidget(record: ReportDashboardWidgetConfigRecord): any {
    if (!record?.WidgetConfigJSON) {
      return null;
    }
    try {
      return typeof record.WidgetConfigJSON === 'string'
        ? JSON.parse(record.WidgetConfigJSON)
        : record.WidgetConfigJSON;
    } catch (_error) {
      return null;
    }
  }

  private resolveWidget(widget: any): any {
    if (!widget?.widgetKey) {
      return widget;
    }
    const record = (this.reusableWidgetRecords || []).find(item => item.WidgetKey === widget.widgetKey);
    const base = this.parseReusableWidget(record) || {};
    return {
      ...base,
      ...(widget.configOverrides || {}),
      key: widget.key || base.key || widget.widgetKey
    };
  }

  private uniqueKey(baseKey: string, existingKeys: string[]): string {
    const normalized = (baseKey || 'new-item').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    let candidate = normalized || 'new-item';
    let suffix = 2;
    while ((existingKeys || []).indexOf(candidate) >= 0) {
      candidate = `${normalized}-${suffix++}`;
    }
    return candidate;
  }

  private clone<T>(value: T): T {
    return JSON.parse(JSON.stringify(value));
  }
}
